import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CopyIcon,
  DeleteIcon,
  GKDataGrid,
  Grid2,
  IconButton,
  StatusField,
  Tooltip,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { toast } from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";
import {
  copyTextToClipboard,
  extraColumnsField,
  matchDate,
} from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const PaymentLinkModal = () => {
  useDocumentTitle("Payment Link");

  const requestBody = useGenerateQueryParams("paymentLink");

  const { data, isFetching, refetch } = useFetchQuery({
    key: ["PAYMENT_LINK_LIST", requestBody],
    route: `/payment-links/`,
    requestBody,
  });

  const { mutate: handleCancelPaymentLink } = useMutation({
    mutationKey: ["CANCEL_PAYMENT_LINK"],
    mutationFn: (rowId: string) =>
      axiosInstance.patch(`/payment-links/${rowId}/cancel/`),
    onSuccess: () => {
      refetch();
    },
  });

  const handleCopyLink = async (linkId: string) => {
    const success = await copyTextToClipboard(linkId);
    if (success) {
      toast.success("Link copied to clipboard!");
    } else {
      toast.error("Failed to copy link");
    }
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Portfolio Name", "clientName", 230, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Invoice", "invoiceNumber", 150, 1),
    },
    {
      ...extraColumnsField("Amount", "amount", 150, 1),
    },
    {
      ...extraColumnsField("Created At", "linkCreatedAt", 200, 1),
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField("Status", "linkStatus", 150, 1),
      renderCell: (params) => {
        return <StatusField statusString={params?.row?.linkStatus} />;
      },
    },
    {
      ...extraColumnsField("Action", "action", 150, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Grid2 container gap={1} display={"flex"} justifyContent={"flex-end"}>
          <Tooltip placement="bottom" title="Copy Payment Link">
            <IconButton
              color="success"
              onClick={() => handleCopyLink(params.row.linkUrl)}
              disabled={
                params.row.linkStatus === "PAID" ||
                params.row.linkStatus === "CANCELLED"
              }
            >
              <CopyIcon width={20} />
            </IconButton>
          </Tooltip>
          <Tooltip placement="bottom" title="Cancel Payment Link">
            <IconButton
              color="error"
              onClick={() => handleCancelPaymentLink(params.row.id)}
              disabled={params.row.linkStatus !== "ACTIVE"}
            >
              <DeleteIcon width={20} />
            </IconButton>
          </Tooltip>
        </Grid2>
      ),
    },
  ];

  return (
    <DashboardLayout title="Payment Link">
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data?.data || []}
        name="paymentLink"
        totalCount={data?.data?.totalCount || 0}
      />
    </DashboardLayout>
  );
};

export default PaymentLinkModal;
